import { apiFetch } from '@/lib/api-client'
import type { ChatMessage, ChatSource } from '@/types/agriculture-api'

export class ChatStreamError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message)
    this.name = 'ChatStreamError'
  }
}

type ChatEvent = { text?: string; error?: string; sources?: ChatSource[] }

/** Go 端单次请求的消息条数与图片总数上限。 */
export const CHAT_MAX_MESSAGES = 20
export const CHAT_MAX_IMAGES = 4
// Stands in for images dropped from older turns so the text-only turn stays non-empty.
const DROPPED_IMAGE_PLACEHOLDER = '[图片]'

/**
 * Fits a conversation into the API window: keeps the latest turns (starting with a user
 * turn) and only the most recent images, stripping older ones from history.
 */
export function buildChatRequest(messages: ChatMessage[]): ChatMessage[] {
  let recent = messages.slice(-CHAT_MAX_MESSAGES)
  while (recent.length > 0 && recent[0].role !== 'user') recent = recent.slice(1)

  let budget = CHAT_MAX_IMAGES
  return recent
    .slice()
    .reverse()
    .map((message) => {
      if (!message.images?.length) return { role: message.role, content: message.content }
      const images = message.images.slice(0, Math.max(0, budget))
      budget -= images.length
      if (images.length) return { role: message.role, content: message.content, images }
      return { role: message.role, content: message.content.trim() || DROPPED_IMAGE_PLACEHOLDER }
    })
    .reverse()
}

export async function streamChat(
  messages: ChatMessage[],
  onDelta: (text: string) => void,
  signal?: AbortSignal,
  onSources?: (sources: ChatSource[]) => void
): Promise<string> {
  const response = await apiFetch('/chat/stream', {
    method: 'POST',
    headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
    signal,
  })
  if (!response.ok) throw new ChatStreamError('Chat request failed', response.status)
  if (!response.body) throw new ChatStreamError('Streaming is unavailable')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let answer = ''
  let done = false

  const processFrame = (frame: string) => {
    const lines = frame.split('\n')
    const event = lines
      .find((line) => line.startsWith('event:'))
      ?.slice(6)
      .trim()
    const data = lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n')
    if (!event || !data) return
    let payload: ChatEvent
    try {
      payload = JSON.parse(data) as ChatEvent
    } catch {
      throw new ChatStreamError('Invalid streaming response')
    }
    if (event === 'error') throw new ChatStreamError('Chat stream interrupted')
    if (event === 'done') {
      done = true
      return
    }
    if (event === 'sources' && Array.isArray(payload.sources)) {
      onSources?.(
        payload.sources.filter(
          (source) =>
            typeof source?.title === 'string' &&
            typeof source?.uri === 'string' &&
            /^https?:\/\//.test(source.uri)
        )
      )
      return
    }
    if (event === 'delta' && typeof payload.text === 'string') {
      answer += payload.text
      onDelta(payload.text)
    }
  }

  try {
    while (!done) {
      const { value, done: streamEnded } = await reader.read()
      buffer = (buffer + decoder.decode(value, { stream: !streamEnded })).replace(/\r\n/g, '\n')
      let separator = buffer.indexOf('\n\n')
      while (separator !== -1) {
        processFrame(buffer.slice(0, separator))
        buffer = buffer.slice(separator + 2)
        if (done) break
        separator = buffer.indexOf('\n\n')
      }
      if (streamEnded) break
    }
  } finally {
    await reader.cancel().catch(() => {})
  }
  if (!done || !answer.trim()) throw new ChatStreamError('Chat stream ended without a reply')
  return answer.trim()
}
